import dayjs, { Dayjs } from "dayjs";
import { Side } from "../types/DepthChartTypes";
import { RosterPlayer } from "../types/LogTypes";
import { Game } from "../types/ScheduleTypes";

export const calculateReturnYardage = (
  caughtOn: number,
  returnedTo: number
): number => {
  const caughtInEndzone = caughtOn === 0;
  const caughtInOwnTerritory = caughtOn < 0;
  const returnedToOwnTerritory = returnedTo < 0;
  const returnedToAbs = Math.abs(returnedTo);
  const caughtOnAbs = 50 - Math.abs(caughtOn);
  const returnedToAbsFrom50 = 50 - returnedToAbs;

  // handle caught in endzone first
  if (caughtInEndzone && returnedToOwnTerritory) {
    return returnedToAbs;
  }
  if (caughtInEndzone && !returnedToOwnTerritory) {
    return returnedToAbsFrom50 + 50;
  }

  // handle caught in own territory
  if (caughtInOwnTerritory && returnedToOwnTerritory) {
    return caughtOnAbs - returnedToAbsFrom50;
  }
  if (caughtInOwnTerritory && !returnedToOwnTerritory) {
    return returnedToAbsFrom50 + caughtOnAbs;
  }

  // handle caught in opponent territory
  if (!caughtInOwnTerritory && returnedToOwnTerritory) {
    return (caughtOnAbs + returnedToAbsFrom50) * -1;
  }
  if (!caughtInOwnTerritory && !returnedToOwnTerritory) {
    return returnedToAbsFrom50 - caughtOnAbs;
  }

  return 0;
};

export const calculateKickoffYardage = (
  kickoffFrom: number,
  kickoffTo: number
): number => {
  const kickedToOpponentTerritory = kickoffTo <= 0;
  const kickedToOwnTerritory = kickoffTo > 0;

  const kickoffFromto50 = 50 - kickoffFrom;
  const kickoffToAbsFrom50 = 50 - Math.abs(kickoffTo);

  // assume it is only possible to kick off from own territory
  // therefore, kickoffFrom is always positive

  // handle kicked into opponent territory
  if (kickedToOpponentTerritory) {
    return kickoffFromto50 + kickoffToAbsFrom50;
  }

  // handle kicked into own territory
  if (kickedToOwnTerritory) {
    return kickoffTo - kickoffFrom;
  }

  return 0;
};

export const sideDisplayName = (side: Side | undefined) => {
  if (!side) {
    return "Unknown";
  }
  if (side === "OFFENSE") {
    return "Offense";
  }
  if (side === "DEFENSE") {
    return "Defense";
  }
  if (side === "KICKOFF") {
    return "Kickoff";
  }
  if (side === "KICKOFF_RETURN") {
    return "Kickoff Return";
  }
  if (side === "PUNT") {
    return "Punt";
  }
  if (side === "PUNT_RETURN") {
    return "Punt Return";
  }
  if (side === "FIELD_GOAL") {
    return "Field Goal";
  }
  if (side === "FIELD_GOAL_BLOCK") {
    return "Field Goal Block";
  }
  return "Unknown";
};

// Helper function to convert MM:SS to total seconds
export const timeToSeconds = (timeStr: string): number => {
  const [minutes, seconds] = timeStr.split(":").map(Number);
  return minutes * 60 + seconds;
};

// Helper function to convert seconds to MM:SS
export const secondsToTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}`;
};

export const mmssToDayjs = (mmss: string) => {
  const [mm, ss] = mmss.split(":").map(Number);
  return dayjs()
    .set("hour", 0)
    .set("minute", mm)
    .set("second", ss)
    .set("millisecond", 0);
};

export const dayjsToMMSS = (d: Dayjs) =>
  `${d.minute().toString().padStart(2, "0")}:${d
    .second()
    .toString()
    .padStart(2, "0")}`;

export const MAX_TIME = mmssToDayjs("12:00");
export const MIN_TIME = mmssToDayjs("00:00");

// Since jersey numbers can be single digits and max two digits and
// we want to these to line up when displayed vertically, we need
// to check if the jersey number is a single digit and if so,
// add a space to the front of the name
export const jerseyNumberNameDisplay = (player: RosterPlayer | undefined) => {
  if (!player) {
    return "Unknown Player";
  }
  const jerseyNumber = player.jersey_number;
  const name = player.player.name;
  if (jerseyNumber && jerseyNumber.toString().length === 1) {
    return ` ${jerseyNumber} ${name}`;
  }
  return `${jerseyNumber} ${name}`;
};

export const didClubWin = (game: Game, clubId: number) => {
  const isHome = game.home_club.id === clubId;
  const clubScore = isHome ? game.home_score : game.away_score;
  const opponentScore = isHome ? game.away_score : game.home_score;

  if (clubScore === null || opponentScore === null) {
    return "";
  }

  if (clubScore > opponentScore) {
    return "W";
  } else if (clubScore < opponentScore) {
    return "L";
  } else {
    return "T";
  }
};
